import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, MailCheck, Send, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { api, formatApiError } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { useCompany } from "../../components/layout/CompanyLayout";
import { PageHeader, Loader, ErrorState } from "../../components/common/States";
import { CompanyStatusBadge } from "../../components/common/Badges";
import { CompanyFieldset, BillingFieldset, validateCompany, EMPTY_COMPANY } from "../../components/company/CompanyFields";
import LogoPicker, { logoUrl } from "../../components/company/LogoPicker";
import { SUB_STATUS } from "../../lib/billingLabels";
import { ROLE, fmtDate } from "../../lib/labels";

const Status = ({ company, can, reload }) => {
  const [busy, setBusy] = useState("");
  const run = async (key, url, msg) => {
    setBusy(key);
    try { await api.post(url); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="card-surface space-y-3 p-5" data-testid="account-status-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-500">État du compte :</span><CompanyStatusBadge value={company.status} />
        <span className={`text-xs ${company.email_verified ? "text-emerald-700" : "text-amber-700"}`} data-testid="account-email-verified">{company.email_verified ? "Email professionnel confirmé" : "Email professionnel non confirmé"}</span>
        {company.status === "active" && <Button asChild variant="outline" size="sm" className="ml-auto" data-testid="settings-public-page-link"><Link to={`/e/${company.slug}`} target="_blank">Page publique <ExternalLink className="ml-1.5 h-3.5 w-3.5" /></Link></Button>}
      </div>
      {company.rejection_reason && company.status === "draft" && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="account-rejection-reason">Demande refusée — motif : {company.rejection_reason}. Corrigez vos informations puis soumettez à nouveau.</p>}
      {["suspended", "terminated"].includes(company.status) && company.status_reason && <p className="text-sm text-rose-700">Motif : {company.status_reason}</p>}
      {can("company:manage") && (
        <div className="flex flex-wrap gap-2">
          {!company.email_verified && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("v", "/company/verification/resend", `Email envoyé à ${company.email}`)} data-testid="resend-verification-button">{busy === "v" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <MailCheck className="mr-1.5 h-3.5 w-3.5" />}Renvoyer l'email de confirmation</Button>}
          {company.status === "draft" && <Button size="sm" disabled={!!busy || !company.email_verified} onClick={() => run("s", "/company/submit", "Demande soumise à validation")} data-testid="submit-review-button">{busy === "s" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}Soumettre à validation</Button>}
          <Button asChild size="sm" variant="ghost" data-testid="create-another-company"><Link to="/inscription-entreprise"><Plus className="mr-1.5 h-3.5 w-3.5" />Créer une autre entreprise</Link></Button>
        </div>
      )}
    </div>
  );
};

const EditForm = ({ company, editable, reload }) => {
  const draft = company.status === "draft";
  const [f, setF] = useState({ ...EMPTY_COMPANY, ...Object.fromEntries(Object.keys(EMPTY_COMPANY).map((k) => [k, company[k] ?? EMPTY_COMPANY[k]])),
    billing: { ...EMPTY_COMPANY.billing, ...(company.billing || {}) }, description: company.description || "", allow_anonymous: company.allow_anonymous !== false });
  const [logo, setLogo] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const errs = validateCompany(f);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Vérifiez les champs signalés"); return; }
    setSaving(true);
    try {
      await api.put("/company/settings", f);
      if (logo) { const fd = new FormData(); fd.append("file", logo); await api.post("/company/logo", fd); setLogo(null); }
      toast.success("Informations enregistrées"); reload();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="card-surface space-y-8 p-6" noValidate>
      <fieldset disabled={!editable} className="space-y-8">
        <div><p className="mb-3 font-display font-semibold">Logo</p><LogoPicker file={logo} onChange={setLogo} current={logoUrl(company)} /></div>
        <CompanyFieldset f={f} setF={setF} errors={errors} lockLegal={!draft} />
        {!draft && <p className="-mt-4 text-xs text-slate-500">Raison sociale et email professionnel modifiables uniquement en brouillon (contactez le support).</p>}
        <div><Label>Description publique</Label><Textarea className="mt-1.5" rows={3} maxLength={1000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="settings-description-input" /></div>
        <label className="flex items-center justify-between gap-4 rounded-lg border p-4"><span className="text-sm"><b>Autoriser les feedbacks anonymes</b></span>
          <Switch checked={f.allow_anonymous} onCheckedChange={(v) => setF({ ...f, allow_anonymous: v })} data-testid="settings-anonymous-switch" /></label>
        {company.billing !== undefined || editable ? <BillingFieldset f={f} setF={setF} errors={errors} /> : null}
      </fieldset>
      {editable ? <Button type="submit" disabled={saving} className="rounded-full" data-testid="settings-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer</Button>
        : <p className="text-sm text-slate-500">Seul le propriétaire peut modifier ces informations.</p>}
    </form>
  );
};

const Overview = ({ data }) => (
  <div className="grid gap-6 lg:grid-cols-2">
    <div className="card-surface p-5" data-testid="account-structure">
      <p className="font-display font-semibold">Structure & contacts</p>
      <div className="mt-3 grid grid-cols-5 gap-2 text-center">
        {[["agencies", "Agences"], ["services", "Services"], ["qrcodes", "QR"], ["memberships", "Équipe"], ["feedbacks", "Feedbacks"]].map(([k, l]) => (
          <div key={k} className="rounded-lg bg-slate-50 p-2"><p className="font-display text-lg font-bold">{data.counts[k]}</p><p className="text-[11px] text-slate-500">{l}</p></div>))}
      </div>
      <ul className="mt-4 space-y-1 text-sm">{data.contacts.map((c) => <li key={c.email}>{c.name} — {c.email} <span className="text-slate-400">({ROLE[c.role]})</span></li>)}</ul>
    </div>
    <div className="card-surface p-5" data-testid="account-subscription">
      <p className="font-display font-semibold">Abonnement & factures</p>
      <p className="mt-3 text-sm">Formule : <b>{data.company.subscription?.plan_name || "Non définie"}</b>{data.company.subscription?.status && ` · ${SUB_STATUS[data.company.subscription.status]?.label}`}</p>
      <p className="mt-2 text-sm text-slate-500">{data.invoices.length} facture(s) émise(s).</p>
      <Link to="/app/abonnement" className="mt-3 inline-block text-sm font-medium text-blue-700" data-testid="account-billing-link">Gérer l'abonnement et les factures</Link>
    </div>
    <div className="card-surface p-5 lg:col-span-2" data-testid="account-history">
      <p className="font-display font-semibold">Historique des actions</p>
      <ul className="mt-3 max-h-80 divide-y overflow-y-auto text-sm">
        {data.history.map((h) => <li key={h.id} className="flex flex-wrap gap-x-3 py-2"><span className="text-slate-400">{fmtDate(h.created_at)}</span><span className="font-mono text-xs">{h.action}</span><span className="text-slate-500">{h.actor_email}</span></li>)}
      </ul>
    </div>
  </div>
);

export default function CompanyAccount() {
  const { can, reload: reloadCtx } = useCompany();
  const { data, loading, error, reload } = useApi("/company/profile");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const refresh = () => { reload(); reloadCtx(); };
  return (
    <div className="space-y-6">
      <PageHeader title="Fiche entreprise" description="Coordonnées, facturation, abonnement, état du compte et historique." />
      <Status company={data.company} can={can} reload={refresh} />
      <Overview data={data} />
      <EditForm key={data.company.logo_path || "x"} company={data.company} editable={can("company:manage")} reload={refresh} />
    </div>
  );
}
